import { formatNumber } from "@/v2-common/utils/utils.formatting"
import {
  SEAT_SOURCE_COMPANION,
  SEAT_SOURCE_FLAT,
  SEAT_SOURCE_QUANTITY,
} from "@/v2-backoffice-client/license/license.constants"
import type { LicensePlanFragment } from "@/v2-backoffice-client/license/plan/__types__/license.plan.fragment"

/**
 * The seat model of a catalog license, read off the catalog list: how its
 * Fenerum price and quantity become a seat count, and which other license
 * the extra seats are sold on. ONE rule, shared by the Licenses board's
 * Seat Model cell, the license panel's "How it's sold" card, the Update
 * License dialog's preview and the customer row's seat tooltip, so they can
 * never tell different stories.
 *
 * Fenerum's catalog has one fixed price per plan term — no price per
 * quantity, no price that depends on the base you hold — so every shape on
 * the price list is MORE PLANS, and the model says how they hang together:
 *
 *   * Base (fixed price, names a seat line): bought once, its price includes
 *     IncludedSeats users, every extra seat is a unit on the seat line it
 *     names (lic.Plans.SeatLinePlanId, a per-seat plan of the same product).
 *     Two bases of one product may name different seat lines, which is how
 *     two per-seat prices live in one product.
 *   * Seat bundle (fixed price, names NO seat line): the included seats are
 *     the cap; a bigger team moves to the next size, the product's other
 *     bundles.
 *   * Seat line (per-seat plan at least one base names) / Per seat (one
 *     nobody names, sold on itself; a unit is IncludedSeats seats, usually
 *     1) / Company-wide (covers the company).
 *
 * It is the client half of api.Sync_Lic_FenerumLicenses' seat sum. The sum
 * itself counts EVERY per-seat plan of the product, not only the one a base
 * names — a seat sold on the wrong line is still a seat the customer pays
 * for — and backoffice.Get_Lic_Licenses flags those (OffLineSeats). Prices
 * are Fenerum's (lic.PlanTerms); nothing here invents one.
 */

export type SeatModelPlan = Pick<
  LicensePlanFragment,
  | "Id"
  | "Name"
  | "ProductCode"
  | "ProductName"
  | "SeatSourceCode"
  | "IncludedSeats"
  | "SeatLinePlanId"
  | "SeatLinePlanName"
  | "DeprecatedAt"
  | "ListPrice"
  | "ListPriceCurrency"
  | "ListPriceIntervalType"
  | "ListPriceIntervalCount"
  | "ActiveTerms"
  | "Mrr"
  | "CustomerCount"
>

type ActiveTerm = SeatModelPlan["ActiveTerms"][number]

export const SEAT_MODEL_KIND = {
  /** No seat semantics declared: customers on it get no seat count. */
  Undeclared: "undeclared",
  /** Fixed price; seats = included seats + the quantity on the seat line it names. */
  Companion: "companion",
  /** Fixed price, names no seat line: the included seats are the cap, a bigger team moves to the next size. */
  Bundle: "bundle",
  /** The per-seat line itself: its quantity is the extra seats of the bases that name it. */
  SeatLine: "seatLine",
  /** Sells its seats on itself, nobody names it: quantity × seats per unit (Propcloud Takst). */
  Quantity: "quantity",
  /** Company-wide: covers the company, nobody is counted. */
  Flat: "flat",
} as const

type SeatModelKind = (typeof SEAT_MODEL_KIND)[keyof typeof SEAT_MODEL_KIND]

export type SeatModel = {
  kind: SeatModelKind
  /** Companion and bundle: the seats the license's own price covers (0 when none declared). */
  includedSeats: number
  /** Per seat and seat line: the seats one unit of the quantity stands for (1 unless declared). */
  seatsPerUnit: number
  /**
   * Companion and flat: the seat line the license names, resolved from the
   * catalog. Null when it names none — or names one the catalog list does
   * not hold (hasUnresolvedSeatLine says which).
   */
  seatLine: SeatModelPlan | null
  /** The license names a seat line that is not in the list: the link is stale. */
  hasUnresolvedSeatLine: boolean
  /** The product's per-seat licenses still sold, by name: what a base CAN name. */
  seatLines: SeatModelPlan[]
  /**
   * Seat line: the licenses that name this one as their seat line. Companion
   * and flat: the OTHER licenses naming the same seat line. Sorted by name.
   */
  basePlans: SeatModelPlan[]
  /**
   * Bundle: the product's sizes — every seat bundle of the product still
   * sold, this one included — smallest first.
   */
  bundles: SeatModelPlan[]
  /** Bundle: where this license sits in `bundles` (0-based), -1 otherwise. */
  bundleIndex: number
}

function isSold(plan: SeatModelPlan) {
  return !plan.DeprecatedAt
}

function byName(a: SeatModelPlan, b: SeatModelPlan) {
  return a.Name.localeCompare(b.Name) || a.Id - b.Id
}

function bySize(a: SeatModelPlan, b: SeatModelPlan) {
  return (
    (a.IncludedSeats ?? 0) - (b.IncludedSeats ?? 0) ||
    (a.ListPrice ?? 0) - (b.ListPrice ?? 0) ||
    byName(a, b)
  )
}

function isBundle(plan: SeatModelPlan) {
  return (
    plan.SeatSourceCode === SEAT_SOURCE_COMPANION && plan.SeatLinePlanId == null
  )
}

/**
 * Resolves a license's seat model from the catalog list the board already
 * holds (~30 rows). `plans` may include the license itself.
 */
export function deriveSeatModel(
  plan: SeatModelPlan,
  plans: readonly SeatModelPlan[],
): SeatModel {
  // The product's other licenses still sold: what shares a seat line and
  // what the sizes of a bundle are. (A deprecated base still naming this
  // license as its seat line is handled below: the mapping proc counts it.)
  const siblings = plans.filter(
    (candidate) =>
      candidate.Id !== plan.Id &&
      candidate.ProductCode != null &&
      candidate.ProductCode === plan.ProductCode &&
      isSold(candidate),
  )
  const seatLines = siblings
    .filter((candidate) => candidate.SeatSourceCode === SEAT_SOURCE_QUANTITY)
    .sort(byName)
  const namedSeatLine =
    plan.SeatLinePlanId == null
      ? null
      : (plans.find((candidate) => candidate.Id === plan.SeatLinePlanId) ??
        null)
  // The other licenses selling their extra seats on the same line.
  const sharing =
    plan.SeatLinePlanId == null
      ? []
      : siblings
          .filter(
            (candidate) => candidate.SeatLinePlanId === plan.SeatLinePlanId,
          )
          .sort(byName)

  const empty: SeatModel = {
    kind: SEAT_MODEL_KIND.Undeclared,
    includedSeats: 0,
    seatsPerUnit: 1,
    seatLine: null,
    hasUnresolvedSeatLine: false,
    seatLines: [],
    basePlans: [],
    bundles: [],
    bundleIndex: -1,
  }

  switch (plan.SeatSourceCode) {
    case SEAT_SOURCE_COMPANION:
      if (plan.SeatLinePlanId == null) {
        const bundles = [plan, ...siblings.filter(isBundle)].sort(bySize)
        return {
          ...empty,
          kind: SEAT_MODEL_KIND.Bundle,
          includedSeats: plan.IncludedSeats ?? 0,
          seatLines,
          bundles,
          bundleIndex: bundles.indexOf(plan),
        }
      }
      return {
        ...empty,
        kind: SEAT_MODEL_KIND.Companion,
        includedSeats: plan.IncludedSeats ?? 0,
        seatLine: namedSeatLine,
        hasUnresolvedSeatLine: namedSeatLine == null,
        seatLines,
        basePlans: sharing,
      }
    case SEAT_SOURCE_QUANTITY: {
      // Every license naming this one as its seat line, deprecated ones
      // included: the mapping proc refuses to move this license while any
      // of them does, so the panel must show them all.
      const dependents = plans
        .filter(
          (candidate) =>
            candidate.Id !== plan.Id && candidate.SeatLinePlanId === plan.Id,
        )
        .sort(byName)
      return {
        ...empty,
        kind:
          dependents.length > 0
            ? SEAT_MODEL_KIND.SeatLine
            : SEAT_MODEL_KIND.Quantity,
        seatsPerUnit: plan.IncludedSeats || 1,
        seatLines,
        basePlans: dependents,
      }
    }
    case SEAT_SOURCE_FLAT:
      return {
        ...empty,
        kind: SEAT_MODEL_KIND.Flat,
        seatLine: namedSeatLine,
        hasUnresolvedSeatLine:
          plan.SeatLinePlanId != null && namedSeatLine == null,
        seatLines,
        basePlans: sharing,
      }
    default:
      return empty
  }
}

/** "/month", "/year", "/3 months" — Fenerum states intervals as type + count. */
export function formatPriceInterval(
  type: string | null | undefined,
  count: number | null | undefined,
) {
  if (!type) return ""
  const n = count ?? 1
  if (n === 1) return `/${type}`
  return `/${n} ${type}s`
}

/** The reference list price as a salesperson says it: "2 000 NOK/month". */
export function formatListPrice(plan: SeatModelPlan) {
  if (plan.ListPrice == null) return null
  const amount = formatNumber(plan.ListPrice)
  const currency = plan.ListPriceCurrency ? ` ${plan.ListPriceCurrency}` : ""
  return `${amount}${currency}${formatPriceInterval(
    plan.ListPriceIntervalType,
    plan.ListPriceIntervalCount,
  )}`
}

/**
 * "1 000 NOK/seat/month" for a per-seat license; "4 000 NOK per 5
 * seats/month" when a unit is more than one seat. Null without a list price.
 */
export function formatSeatLinePrice(seatLine: SeatModelPlan) {
  if (seatLine.ListPrice == null) return null
  const amount = formatNumber(seatLine.ListPrice)
  const currency = seatLine.ListPriceCurrency
    ? ` ${seatLine.ListPriceCurrency}`
    : ""
  const perUnit = seatLine.IncludedSeats || 1
  const unit = perUnit > 1 ? ` per ${perUnit} seats` : "/seat"
  return `${amount}${currency}${unit}${formatPriceInterval(
    seatLine.ListPriceIntervalType,
    seatLine.ListPriceIntervalCount,
  )}`
}

/**
 * What one seat costs on a seat bundle: its list price over its included
 * seats ("800 NOK/seat/month" on a 40-seat bundle at 32 000). Null without
 * a price or without seats.
 */
export function formatBundleSeatPrice(bundle: SeatModelPlan) {
  if (bundle.ListPrice == null || !bundle.IncludedSeats) return null
  const amount = formatNumber(bundle.ListPrice / bundle.IncludedSeats)
  const currency = bundle.ListPriceCurrency
    ? ` ${bundle.ListPriceCurrency}`
    : ""
  return `${amount}${currency}/seat${formatPriceInterval(
    bundle.ListPriceIntervalType,
    bundle.ListPriceIntervalCount,
  )}`
}

function seatWord(count: number) {
  return count === 1 ? "seat" : "seats"
}

/** "quantity = seats" or "each unit is 5 seats". */
function unitSentence(seatsPerUnit: number) {
  return seatsPerUnit > 1
    ? `each unit is ${seatsPerUnit} seats`
    : "quantity = seats"
}

export function licenseWord(count: number) {
  return count === 1 ? "license" : "licenses"
}

/** The short word per kind — the board's Seat Model cell, its filter and the panel's info row. */
export const SEAT_MODEL_LABEL: Record<SeatModelKind, string> = {
  [SEAT_MODEL_KIND.Undeclared]: "Not set",
  [SEAT_MODEL_KIND.Companion]: "Base",
  [SEAT_MODEL_KIND.Bundle]: "Seat bundle",
  [SEAT_MODEL_KIND.SeatLine]: "Seat line",
  [SEAT_MODEL_KIND.Quantity]: "Per seat",
  [SEAT_MODEL_KIND.Flat]: "Company-wide",
}

export type SeatModelText = {
  label: string
  /** The rest of the sentence, null when the label says it all. */
  detail: string | null
}

/**
 * What a customer's seat count is under this license, as a label and the
 * detail behind it. Written for the salesperson scanning the board; the
 * panel and the dialog read the same words.
 */
export function describeSeatModel(model: SeatModel): SeatModelText {
  const label = SEAT_MODEL_LABEL[model.kind]
  switch (model.kind) {
    case SEAT_MODEL_KIND.Companion: {
      const included =
        model.includedSeats > 0
          ? `${model.includedSeats} ${seatWord(model.includedSeats)} included`
          : "no seats included"
      if (!model.seatLine) {
        return {
          label,
          detail: `${included}, names a seat line that is no longer in the catalog`,
        }
      }
      const price = formatSeatLinePrice(model.seatLine)
      return {
        label,
        detail: `${included}, extra seats on ${model.seatLine.Name}${price ? ` at ${price}` : ""}`,
      }
    }
    case SEAT_MODEL_KIND.Bundle: {
      if (model.includedSeats === 0) {
        return {
          label,
          detail:
            "no seats included and no seat line, so customers get no seats",
        }
      }
      const sizes =
        model.bundles.length > 1 && model.bundleIndex >= 0
          ? `size ${model.bundleIndex + 1} of ${model.bundles.length}`
          : "the only size"
      return {
        label,
        detail: `${model.includedSeats} ${seatWord(model.includedSeats)} for one price (${sizes}); a bigger team moves to the next size`,
      }
    }
    case SEAT_MODEL_KIND.SeatLine:
      return {
        label,
        detail: `${unitSentence(model.seatsPerUnit)}, the extra seats of ${model.basePlans.length} ${licenseWord(model.basePlans.length)}`,
      }
    case SEAT_MODEL_KIND.Quantity:
      return {
        label,
        detail: `${unitSentence(model.seatsPerUnit)}, sold on its own`,
      }
    case SEAT_MODEL_KIND.Flat:
      return {
        label,
        detail: model.seatLine
          ? `one price for the whole company, no cap (seats on ${model.seatLine.Name} still count)`
          : "one price for the whole company, no cap",
      }
    default:
      return {
        label,
        detail: "the license works, customers just get no seat cap",
      }
  }
}

/** The label and detail as one line: "Base · 1 seat included, extra seats on …". */
export function seatModelSentence(model: SeatModel) {
  const { label, detail } = describeSeatModel(model)
  return detail ? `${label} · ${detail}` : label
}

/**
 * The bundle size a team of `seats` lands on: the smallest one with room.
 * Null when the model is not a seat bundle, or the team outgrows the largest
 * size (then the price is by agreement).
 */
export function fitBundle(model: SeatModel, seats: number) {
  if (model.kind !== SEAT_MODEL_KIND.Bundle) return null
  return (
    model.bundles.find((bundle) => (bundle.IncludedSeats ?? 0) >= seats) ?? null
  )
}

type SeatModelQuoteLine = {
  label: string
  quantity: number
  unitPrice: number
  amount: number
}

type SeatModelQuote = {
  lines: SeatModelQuoteLine[]
  total: number
  currency: string | null
  interval: string
  /**
   * The seat line sells no term on the base's interval, so both were
   * converted to a month (price ÷ months) to add up; the card says so.
   */
  isMonthlyEquivalent: boolean
  /** Bundle only: the size the team landed on. */
  bundle: SeatModelPlan | null
}

/** Months in a Fenerum interval; null for one that cannot be converted. */
function monthsOfInterval(
  type: string | null | undefined,
  count: number | null | undefined,
) {
  const n = count ?? 1
  switch (type) {
    case "year":
      return 12 * n
    case "month":
      return n
    case "day":
      return n / 30
    default:
      return null
  }
}

/** The plan's active term on exactly this interval and currency, if it sells one. */
function findTerm(
  plan: SeatModelPlan,
  intervalType: string | null | undefined,
  intervalCount: number | null | undefined,
  currency: string | null,
): ActiveTerm | null {
  return (
    plan.ActiveTerms.find(
      (term) =>
        term.IntervalType === intervalType &&
        (term.IntervalCount ?? 1) === (intervalCount ?? 1) &&
        (term.Currency ?? null) === currency &&
        term.Price != null,
    ) ?? null
  )
}

/**
 * The worked example on the license panel: what a customer with `seats`
 * users pays at list price under this model — base once, then the seats
 * beyond the included ones on the seat line; a seat bundle, the size the
 * team fits on; per seat, as many units as the team needs. Fenerum's list
 * prices only, so a discount or contract
 * tier agreed in Fenerum is not in it (the card says so). The seat line is
 * priced on the base's own interval when it sells a term there (Enterprise
 * quarterly + Bruker quarterly); otherwise both are converted to a month
 * and the quote says so. Null when the base has no list price, the seat
 * line none at all, the currencies differ, the team outgrows every bundle
 * size, or the model has no price shape to quote (company-wide, not set).
 */
export function quoteSeatModel(input: {
  plan: SeatModelPlan
  model: SeatModel
  seats: number
}): SeatModelQuote | null {
  const { plan, model, seats } = input

  if (model.kind === SEAT_MODEL_KIND.Bundle) {
    const bundle = fitBundle(model, seats)
    if (!bundle || bundle.ListPrice == null) return null
    return {
      lines: [
        {
          label: bundle.Name,
          quantity: 1,
          unitPrice: bundle.ListPrice,
          amount: bundle.ListPrice,
        },
      ],
      total: bundle.ListPrice,
      currency: bundle.ListPriceCurrency ?? null,
      interval: formatPriceInterval(
        bundle.ListPriceIntervalType,
        bundle.ListPriceIntervalCount,
      ),
      isMonthlyEquivalent: false,
      bundle,
    }
  }

  if (plan.ListPrice == null) return null
  const interval = formatPriceInterval(
    plan.ListPriceIntervalType,
    plan.ListPriceIntervalCount,
  )
  const currency = plan.ListPriceCurrency ?? null

  if (model.kind === SEAT_MODEL_KIND.Quantity) {
    // As many units as the team needs: a five-seat pack for 7 people is 2.
    const units = Math.ceil(seats / model.seatsPerUnit)
    const amount = plan.ListPrice * units
    return {
      lines: [
        {
          label: plan.Name,
          quantity: units,
          unitPrice: plan.ListPrice,
          amount,
        },
      ],
      total: amount,
      currency,
      interval,
      isMonthlyEquivalent: false,
      bundle: null,
    }
  }

  if (model.kind !== SEAT_MODEL_KIND.Companion) return null

  const baseLine: SeatModelQuoteLine = {
    label: plan.Name,
    quantity: 1,
    unitPrice: plan.ListPrice,
    amount: plan.ListPrice,
  }
  const extraSeats = Math.max(0, seats - model.includedSeats)
  const seatLine = model.seatLine
  // Units on the seat line: a seat line whose unit is five seats sells
  // seven extra seats as two units.
  const extraUnits = seatLine
    ? Math.ceil(extraSeats / (seatLine.IncludedSeats || 1))
    : extraSeats

  // The base alone: nothing to reconcile.
  if (extraSeats === 0) {
    return {
      lines: [baseLine],
      total: baseLine.amount,
      currency,
      interval,
      isMonthlyEquivalent: false,
      bundle: null,
    }
  }
  if (!seatLine || (seatLine.ListPriceCurrency ?? null) !== currency) {
    return null
  }

  // Same interval when the seat line sells one there…
  const matchingTerm = findTerm(
    seatLine,
    plan.ListPriceIntervalType,
    plan.ListPriceIntervalCount,
    currency,
  )
  if (matchingTerm?.Price != null) {
    const seatLineLine: SeatModelQuoteLine = {
      label: seatLine.Name,
      quantity: extraUnits,
      unitPrice: matchingTerm.Price,
      amount: matchingTerm.Price * extraUnits,
    }
    return {
      lines: [baseLine, seatLineLine],
      total: baseLine.amount + seatLineLine.amount,
      currency,
      interval,
      isMonthlyEquivalent: false,
      bundle: null,
    }
  }

  // …else both brought to a month.
  const baseMonths = monthsOfInterval(
    plan.ListPriceIntervalType,
    plan.ListPriceIntervalCount,
  )
  const seatMonths = monthsOfInterval(
    seatLine.ListPriceIntervalType,
    seatLine.ListPriceIntervalCount,
  )
  if (!baseMonths || !seatMonths || seatLine.ListPrice == null) return null
  const baseMonthly = plan.ListPrice / baseMonths
  const seatMonthly = seatLine.ListPrice / seatMonths
  const lines: SeatModelQuoteLine[] = [
    {
      label: plan.Name,
      quantity: 1,
      unitPrice: baseMonthly,
      amount: baseMonthly,
    },
    {
      label: seatLine.Name,
      quantity: extraUnits,
      unitPrice: seatMonthly,
      amount: seatMonthly * extraUnits,
    },
  ]
  return {
    lines,
    total: lines.reduce((sum, line) => sum + line.amount, 0),
    currency,
    interval: "/month",
    isMonthlyEquivalent: true,
    bundle: null,
  }
}
